import React, { useRef, useEffect, useState } from "react";
import { Box, Typography, Button, Stack, Grid } from "@mui/material";
import TypewriterText from "../components/TypewriterText";
import AtomicLogo from "../components/AtomicLogo";
import QuickLinksFloating from "../components/QuickLinksFloating";
import MobileHeroFooter from "../components/MobileHeroFooter";
import CurrentlyBuilding from "../components/CurrentlyBuilding";
import EngagementCard from "../components/EngagementCard";
import TechStackCard from "../components/TechStackCard";
import FeaturedProjectCard from "../components/FeaturedProjectCard";
import More from "../components/More";
import TechIntro from "./TechIntro";

// ─── Infinite looping carousel ───────────────────────────────────────────────
// Technique: render [clone tail | real items | clone head], start scrolled to
// the real items, and silently jump back/forward when the clones are reached.
const CARD_WIDTH_PCT = 0.76; // 76% of container
const GAP = 10; // px gap between cards

const InfiniteCarousel = () => {
  const cards = [EngagementCard, CurrentlyBuilding, TechStackCard, FeaturedProjectCard];
  const COUNT = cards.length;

  const scrollRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const isJumping = useRef(false);

  // On mount: jump to the first real item (after the cloned tail)
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const cardW = el.offsetWidth * CARD_WIDTH_PCT + GAP;
    // Cloned tail = COUNT cards, so real[0] is at index COUNT
    el.scrollLeft = cardW * COUNT;
  }, []);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || isJumping.current) return;
    const cardW = el.offsetWidth * CARD_WIDTH_PCT + GAP;
    const total = COUNT * 3; // tail + real + head clones
    const maxScroll = cardW * (total - 1);

    // Detect which real card is centered
    const centeredIndex = Math.round(el.scrollLeft / cardW);
    const realIdx = ((centeredIndex - COUNT) % COUNT + COUNT) % COUNT;
    setActiveIdx(realIdx);

    // Jump: if we've scrolled into the cloned head (past real items)
    if (el.scrollLeft >= cardW * (COUNT * 2)) {
      isJumping.current = true;
      el.scrollLeft -= cardW * COUNT;
      isJumping.current = false;
    }
    // Jump: if we've scrolled into the cloned tail (before real items)
    if (el.scrollLeft <= cardW * (COUNT - 1)) {
      isJumping.current = true;
      el.scrollLeft += cardW * COUNT;
      isJumping.current = false;
    }
  };

  // Build: [cloned tail (last N)] + [real] + [cloned head (first N)]
  const allCards = [...cards.slice(-COUNT), ...cards, ...cards.slice(0, COUNT)];

  return (
    <Box sx={{ position: "relative", overflow: "hidden" }}>
      {/* fade mask */}
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          zIndex: 2,
          WebkitMaskImage:
            "linear-gradient(to right, transparent 0%, black 14%, black 86%, transparent 100%)",
          maskImage:
            "linear-gradient(to right, transparent 0%, black 14%, black 86%, transparent 100%)",
          background:
            "linear-gradient(to right, #0f172a 0%, transparent 14%, transparent 86%, #0f172a 100%)",
        }}
      />

      {/* scroll track */}
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: `${GAP}px`,
          px: "12%",
          overflowX: "scroll",
          msOverflowStyle: "none",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          scrollSnapType: "x mandatory",
          "& > *": {
            flexShrink: 0,
            scrollSnapAlign: "center",
            width: `${CARD_WIDTH_PCT * 100}%`,
          },
        }}
      >
        {allCards.map((Card, i) => (
          <Box key={i}>
            <Card />
          </Box>
        ))}
      </Box>

      {/* Active dots */}
      <Stack direction="row" justifyContent="center" spacing={1} sx={{ mt: 1.5 }}>
        {cards.map((_, i) => (
          <Box
            key={i}
            sx={{
              width: i === activeIdx ? 18 : 6,
              height: 6,
              borderRadius: "100px",
              bgcolor: "#3b82f6",
              opacity: i === activeIdx ? 1 : 0.3,
              boxShadow: i === activeIdx ? "0 0 8px rgba(59,130,246,0.8)" : "none",
              transition: "all 0.25s ease",
            }}
          />
        ))}
      </Stack>

      <Typography
        variant="caption"
        sx={{
          display: "block",
          textAlign: "center",
          color: "rgba(255,255,255,0.3)",
          mt: 0.5,
          mb: 1,
          letterSpacing: 1,
          fontSize: "9px",
        }}
      >
        SWIPE TO EXPLORE
      </Typography>
    </Box>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const Hero = () => {
  return (
    <Box
      component="section"
      id="hero"
      sx={{
        position: "relative",
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        bgcolor: "#0f172a",
      }}
    >
      {/* ─── MAIN CONTENT LAYER ─── */}
      {/* Desktop: takes flex:8 | Mobile: takes flex:4 (shared with intro+swipe below) */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          flex: { xs: 4, md: 4, lg: 8 },
          display: "flex",
          alignItems: "center",
          px: { xs: 2, md: 6, lg: 8 },
          mx: "auto",
          width: "95%",
          mt: 10,
          mb: 2,
          overflow: "hidden",
          borderRadius: "40px",
        }}
      >
        {/* Video bg */}
        <Box
          component="video"
          autoPlay loop muted playsInline
          src="/network_animation.mp4"
          sx={{
            position: "absolute",
            inset: 0,
            height: "100%",
            width: "100%",
            objectFit: "cover",
            opacity: 0.25,
            zIndex: -1,
          }}
        />

        {/* Grid bg */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            opacity: 0.75,
            zIndex: -1,
            backgroundImage: `linear-gradient(rgba(59,130,246,0.1) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(59,130,246,0.1) 1px, transparent 1px)`,
            backgroundSize: "40px 40px",
            maskImage: "radial-gradient(ellipse at center, black, transparent 80%)",
            pointerEvents: "none",
          }}
        />

        {/* Glass layer */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: -1,
            border: "1px solid rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.03)",
            backdropFilter: "blur(12px)",
            boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
            pointerEvents: "none",
          }}
        />

        {/* Content grid */}
        <Grid
          container
          alignItems="center"
          spacing={4}
          sx={{
            position: "relative",
            zIndex: 20,
            mt: { sm: -20, md: -20, lg: 0 },
          }}
        >
          {/* LEFT */}
          <Grid item xs={12} md={7}>
            <Box
              sx={{
                fontSize: {
                  xs: "clamp(2rem, 8vw, 3.5rem)",
                  md: "clamp(3.5rem, 5vw, 6rem)",
                },
                "@media (max-height: 600px)": {
                  fontSize: "clamp(1.5rem, 10vh, 2.5rem)",
                  lineHeight: 1.1,
                },
                textAlign: { xs: "center", md: "left" },
                ml: { xs: 0, md: 4, lg: 20 },
                mt: { xs: 4, md: 0, lg: 10 },
              }}
            >
              <Typography
                variant="h1"
                sx={{ fontWeight: 900, color: "#0adee6", lineHeight: 1, mb: 1, ml: 10 }}
              >
                ATOMIC
              </Typography>

              <Typography
                variant="h2"
                sx={{
                  fontSize: { xs: "28px", md: "50px", lg: "64px" },
                  fontWeight: 800,
                  color: "#e6edf3",
                  mb: 2,
                }}
              >
                Tech. Solutions
              </Typography>

              <Box sx={{ minHeight: 60 }}>
                <TypewriterText
                  phrases={[
                    "Full-Stack Web Development",
                    "Cloud Security & DevSecOps",
                    "Generative Engine Optimization",
                    "Building Scalable, Secure Systems",
                    "Search Engine Optimization",
                  ]}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 4, justifyContent: { xs: "center", md: "flex-start" } }}
              >
                <Button
                  variant="contained"
                  href="#services"
                  sx={{
                    px: 4, py: 1.5, borderRadius: "12px", bgcolor: "#3b82f6",
                    "&:hover": { bgcolor: "#2563eb" },
                  }}
                >
                  Trading BOTS
                </Button>
                <Button
                  variant="outlined"
                  href="#webdesign"
                  sx={{
                    px: 4, py: 1.5, borderRadius: "12px", color: "#fff", borderColor: "#3b82f6",
                    "&:hover": { borderColor: "#60a5fa", bgcolor: "rgba(59,130,246,0.1)" },
                  }}
                >
                  Web Design
                </Button>
              </Stack>
            </Box>
          </Grid>

          {/* RIGHT — Logo */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Box
                sx={{
                  width: { xs: 180, md: 350 },
                  height: { xs: 180, md: 350 },
                  borderRadius: "50%",
                  border: "2px solid rgba(59,130,246,0.3)",
                  background: "rgba(59,130,246,0.05)",
                  boxShadow: "0 0 60px rgba(59,130,246,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <AtomicLogo />
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* ─── MOBILE ONLY: TechIntro + Swipe Cards + Footer ─── */}
      {/*
        On mobile (xs/md): this wrapper takes flex:5 and splits the remaining
        height between TechIntro and the swipe cards — both in normal flow,
        no absolute positioning fighting the parent.
        On desktop (lg+): hidden entirely (display:none).
      */}
      <Box
        sx={{
          display: { xs: "flex", lg: "none" },
          flexDirection: "column",
          flex: { xs: 5, md: 5 },
          overflow: "hidden",
          zIndex: 20,
          px: 2,
        }}
      >
        {/* TechIntro — takes natural height */}
        <Box sx={{ flexShrink: 0 }}>
          <TechIntro />
        </Box>

        {/* Swipe cards — fills remaining space */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            overflow: "hidden",
            mt: 1,
          }}
        >
          <InfiniteCarousel />
        </Box>
      </Box>

      {/* ─── FOOTER LAYER ─── */}
      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 40,
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "center",
        }}
      >
        <MobileHeroFooter />
      </Box>

      {/* ─── FLOATING ELEMENTS ─── */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 10, sm: 10, md: 120, lg: 30 },
          right: { xs: 10, sm: 10, md: -5, lg: 30 },
          zIndex: 50,
        }}
      >
        <More />
      </Box>
    </Box>
  );
};

export default Hero;